import './theme/shared.css';

import { IonApp, setupIonicReact } from '@ionic/react';
import { FC, useEffect } from 'react';
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import './theme/variables.css';
import './theme/typography.css';
import './theme/utils.css';
import './theme/overrides.css';
import './theme/animations.css';
import { useStart } from './api/hooks/useStart';
import { useApp } from './api/hooks/useApp';
import { useMenu } from './api/hooks/useMenu';
import { useUserState } from './hooks/context/useUserState';
import {
  LOG_OUT,
  SET_API_ENDPOINT,
  SET_SESSION_ERROR_PENDING,
  SET_BRANCH_NAME,
  SET_VENUE_CONTEXT,
} from './constants';
import { getIsMobile } from './utils/helpers';
import { useCfgParam } from './hooks/useCfgParam';
import apiEndpoints from './apiEndpoints';
import useApiErrorHandling from './hooks/useApiErrorHandling';
import Router from './router/Router';
import { requestVenueContext } from './api/requests/requestVenueContext';

setupIonicReact({ mode: 'md' });

const App: FC = () => {
  const { isLoggedIn, error, start, isPending } = useStart();
  const { getApp } = useApp();
  const { getMenu } = useMenu();
  const apiEndpoint = apiEndpoints.basePath;
  const handleApiError = useApiErrorHandling();
  const {
    dispatch,
    state: { token, endpointCorrect, menuItems, menuId },
  } = useUserState();
  const configBranchName = useCfgParam('branchName');

  const isMobile = getIsMobile();
  const startSession = async () => {
    try {
      await start();
    } catch (e) {
      handleApiError(e);
    }
  };

  const initLoggedInSession = async () => {
    if (!token) return;
    dispatch({
      type: SET_VENUE_CONTEXT,
      sessionToken: token,
      payload: undefined,
    });
    try {
      const venueContext = await requestVenueContext(token);
      dispatch({
        type: SET_VENUE_CONTEXT,
        sessionToken: token,
        payload: venueContext.venueName,
      });
    } catch {
      dispatch({ type: SET_VENUE_CONTEXT, sessionToken: token, payload: null });
    }
    try {
      const appResponse = await getApp({ sessionToken: token });
      if (!menuItems.length && appResponse?.mainMenu?.menuId) {
        await getMenu({
          sessionToken: token,
          menuId: appResponse.mainMenu.menuId,
        });
      }
    } catch (e) {
      handleApiError(e);
    }
  };
  useEffect(() => {
    dispatch({ type: SET_SESSION_ERROR_PENDING, payload: false });
    if (!isMobile) {
      dispatch({
        type: SET_BRANCH_NAME,
        payload: configBranchName,
      });
    }
    if (apiEndpoint) {
      if (isMobile && !endpointCorrect) {
        dispatch({
          type: SET_API_ENDPOINT,
          apiEndpoint: '',
          branchName: '',
        });
      } else if (!token) {
        startSession();
      } else if (isLoggedIn) {
        initLoggedInSession();
      } else if (!menuId) {
        getApp({ sessionToken: token }).catch((e) => handleApiError(e));
      }
    }
    if (isLoggedIn && !apiEndpoint) {
      dispatch({ type: LOG_OUT });
    }
  }, []);

  return (
    <IonApp>
      <Router
        hasError={error !== null}
        isPending={isPending}
        isLoggedIn={isLoggedIn ?? false}
      />
    </IonApp>
  );
};

export default App;
